/**
 * De server die zelf even belt.
 *
 * Tot nu toe vroeg elke browser om de paar seconden of er nieuws was. Zeven timers
 * naast elkaar, ruim veertig verzoeken per minuut per open tabblad, en toch zag je
 * een nieuw bericht pas een tel of drie later. PocketBase kan het omdraaien: je
 * opent één verbinding die openblijft, en de server stuurt een regeltje zodra er
 * iets verandert.
 *
 * De opzet hier is met opzet voorzichtig. Zo'n binnenkomend regeltje bevat het hele
 * gewijzigde record, maar daar doen we niets mee. Het is puur een tikje op de
 * schouder: "haal de chat maar op". Alle bestaande ophaal- en samenvoegcode blijft
 * dus precies zoals hij was. Valt de verbinding weg, dan nemen de timers het over
 * en merkt niemand er iets van. Dat is de reden dat de timers blijven bestaan in
 * plaats van dat ze verdwijnen.
 *
 * EventSource en fetch komen van buiten binnen, zodat de tests dit kunnen draaien
 * zonder browser en zonder server.
 */

/** Zoveel wachten voor we opnieuw proberen, oplopend tot een halve minuut. */
const WACHT_MIN = 1000;
const WACHT_MAX = 30000;

export function createLive(opts) {
  const url = String(opts.url || '').replace(/\/+$/, '');
  const collections = opts.collections || [];
  const onEvent = opts.onEvent || (() => {});
  const onStatus = opts.onStatus || (() => {});
  const ES = opts.EventSourceImpl || (typeof EventSource !== 'undefined' ? EventSource : null);
  const doFetch = opts.fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
  const zetTimer = opts.setTimeoutImpl || setTimeout;
  const wisTimer = opts.clearTimeoutImpl || clearTimeout;

  let es = null;
  let clientId = '';
  let live = false;
  let gestopt = true;
  let wacht = WACHT_MIN;
  let hertimer = null;

  const meld = (nu) => {
    if (nu === live) return;
    live = nu;
    try { onStatus(nu); } catch (e) {}
  };

  /**
   * De abonnementen doorgeven. Dit hoort na élke PB_CONNECT: verliest de browser
   * de verbinding en maakt hij hem opnieuw, dan krijg je een nieuw clientId en is
   * de server je oude abonnementen vergeten.
   */
  const abonneer = async (id) => {
    if (!doFetch) return;
    try {
      const res = await doFetch(url + '/api/realtime', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId: id, subscriptions: collections })
      });
      if (gestopt || id !== clientId) return;
      // 204 is goed nieuws. Alles daarbuiten betekent: laat de timers maar lopen.
      if (res && (res.status === 204 || res.ok)) { wacht = WACHT_MIN; meld(true); }
      else meld(false);
    } catch (e) {
      meld(false);
    }
  };

  const bericht = (ev) => {
    if (gestopt) return;
    const naam = ev && ev.type ? ev.type : 'message';
    let data;
    try { data = ev && ev.data ? JSON.parse(ev.data) : null; } catch (e) { data = null; }
    if (naam === 'PB_CONNECT') {
      clientId = (data && data.clientId) || '';
      if (clientId) abonneer(clientId);
      return;
    }
    // De naam van de gebeurtenis is de collectie waarop je geabonneerd bent.
    if (!collections.includes(naam)) return;
    meld(true);
    try { onEvent(naam, data); } catch (e) {}
  };

  const stuk = () => {
    if (gestopt) return;
    meld(false);
    // EventSource probeert het zelf opnieuw, maar niet als de server de verbinding
    // netjes afsloot: dan gaat hij dicht en moeten wij hem heropenen.
    if (es && es.readyState === 2) {
      sluit();
      wisTimer(hertimer);
      hertimer = zetTimer(() => { if (!gestopt) open(); }, wacht);
      wacht = Math.min(WACHT_MAX, wacht * 2);
    }
  };

  const sluit = () => {
    if (!es) return;
    try { es.close(); } catch (e) {}
    es = null;
  };

  const open = () => {
    if (!ES || !url || !collections.length) return;
    sluit();
    clientId = '';
    try {
      es = new ES(url + '/api/realtime');
    } catch (e) {
      es = null;
      meld(false);
      return;
    }
    es.onerror = stuk;
    es.onmessage = bericht;
    // PocketBase stuurt elke collectie onder haar eigen naam, en de handdruk als
    // PB_CONNECT. Die hebben allemaal hun eigen luisteraar nodig; onmessage vangt
    // alleen berichten zónder naam op.
    const luister = (naam) => {
      if (typeof es.addEventListener !== 'function') return;
      es.addEventListener(naam, bericht);
    };
    luister('PB_CONNECT');
    collections.forEach(luister);
  };

  return {
    start() {
      if (!gestopt) return;
      gestopt = false;
      wacht = WACHT_MIN;
      open();
    },
    stop() {
      gestopt = true;
      wisTimer(hertimer);
      hertimer = null;
      sluit();
      meld(false);
    },
    isLive() { return live; },
    /** Alleen voor de tests: welk clientId denken we te hebben. */
    _clientId() { return clientId; }
  };
}
